/* eslint-disable import/no-duplicates */
import fhirpath from 'fhirpath';
import ptBR from 'date-fns/locale/pt-BR';
import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addSeconds,
  addWeeks,
  addYears,
  format,
  getHours,
  getMinutes,
  parseISO,
} from 'date-fns';

const REQUESTED_INTERVAL_EXTENSION =
  'http://healthit.medflowapp/fhir/StructureDefinition/appointment-requested-interval';

type UnitOfTime = 's' | 'min' | 'h' | 'd' | 'wk' | 'mo' | 'a';

function isUnitOfTime(value: unknown): value is UnitOfTime {
  return (
    value === 's' ||
    value === 'min' ||
    value === 'h' ||
    value === 'd' ||
    value === 'wk' ||
    value === 'mo' ||
    value === 'a'
  );
}

function hasDurationValueAndCode(
  duration: fhir4.Duration,
): duration is fhir4.Duration & {
  value: number;
  code: UnitOfTime;
} {
  return typeof duration.value === 'number' && isUnitOfTime(duration.code);
}

export function addUnitsOfTime(
  date: Date,
  unit: UnitOfTime,
  amount: number,
): Date {
  const addFunc: Record<UnitOfTime, (date: Date, amount: number) => Date> = {
    s: addSeconds,
    min: addMinutes,
    h: addHours,
    d: addDays,
    wk: addWeeks,
    mo: addMonths,
    a: addYears,
  };

  const func = addFunc[unit];

  if (func) {
    return func(date, amount);
  }

  return date;
}

export function appointmentRequestedDuration(
  appointment: fhir4.Appointment,
): fhir4.Duration[] {
  return fhirpath.evaluate(
    appointment,
    `Appointment.extension.where(url='${REQUESTED_INTERVAL_EXTENSION}').valueDuration`,
  );
}

function dateOption(date: Date): { value: string; label: string } {
  return {
    value: format(date, 'yyyy-MM-dd'),
    label: format(date, "d 'de' MMMM 'de' yyyy (EEEE)", {
      locale: ptBR,
    }),
  };
}

export function appointmentDateTime(
  appointment: fhir4.Appointment,
): Date | undefined {
  if (appointment.start) {
    return parseISO(appointment.start);
  }

  const requestedDuration: fhir4.Duration = appointmentRequestedDuration(
    appointment,
  )[0];

  if (requestedDuration && hasDurationValueAndCode(requestedDuration)) {
    const now = new Date();

    return addUnitsOfTime(now, requestedDuration.code, requestedDuration.value);
  }

  if (appointment.requestedPeriod && appointment.requestedPeriod.length > 0) {
    const periodStart = appointment.requestedPeriod[0].start;

    if (periodStart) {
      return parseISO(periodStart);
    }
  }

  return undefined;
}

export function appointmentDateTimeDisplay(
  appointment: fhir4.Appointment,
): string {
  const date = appointmentDateTime(appointment);

  if (date) {
    const hasDisplayTime =
      appointment.start && !(getHours(date) === 0 && getMinutes(date) === 0);

    const formatString = hasDisplayTime
      ? "d 'de' MMMM 'de' yyyy (EEEE) 'às' HH:mm"
      : "d 'de' MMMM 'de' yyyy (EEEE)";

    // Format "10 de Abril de 2026 (terça-feira) às 14:30"
    return format(date, formatString, {
      locale: ptBR,
    });
  }

  return 'Sem data';
}

export function appointmentServiceType(appointment: fhir4.Appointment): string {
  return (
    fhirpath.evaluate(
      appointment,
      'Appointment.serviceType.select((text | coding.display).first())',
    )[0] || 'Agendamento'
  );
}

export function appointmentDateOptions(
  appointment: fhir4.Appointment,
): { value: string; label: string }[] {
  const requestedIntervalDuration: fhir4.Duration[] = appointmentRequestedDuration(
    appointment,
  );

  if (requestedIntervalDuration.length) {
    return requestedIntervalDuration
      .filter(hasDurationValueAndCode)
      .map(duration => {
        const now = new Date();
        const date = addUnitsOfTime(now, duration.code, duration.value);

        return dateOption(date);
      });
  }

  if (appointment.requestedPeriod && appointment.requestedPeriod.length) {
    return appointment.requestedPeriod
      .map(period => {
        if (period.start) {
          const date = parseISO(period.start);
          return dateOption(date);
        }

        return null;
      })
      .filter(
        (option): option is { value: string; label: string } => option !== null,
      );
  }

  return [];
}
